import { HttpClient } from '@angular/common/http';
import { Injectable, SecurityContext, inject } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Observable, catchError, from, map, of, switchMap } from 'rxjs';
import {
  DocsManifest,
  DocsManifestState,
  DocsNavItem,
  DocsPageState,
} from 'src/app/Models/docs.model';

// Keep docs URLs relative to <base href> so they work in both root and virtual-directory deployments.
const DOCS_BASE_URL: string = '/docs';
const DOCS_MANIFEST_URL: string = `${DOCS_BASE_URL}/docs-manifest.json`;
const DOCS_BASE_URL_FALLBACK: string = 'docs';
export const DOCS_DEFAULT_PAGE: string = `${DOCS_BASE_URL}/getting-started/introduction`;

@Injectable({ providedIn: 'root' })
export class DocsService {
  private readonly http = inject(HttpClient);
  private readonly sanitizer = inject(DomSanitizer);

  getManifest(): Observable<DocsManifestState> {
    return this.http.get<DocsManifest>(DOCS_MANIFEST_URL).pipe(
      catchError(() => this.http.get<DocsManifest>(`${DOCS_BASE_URL_FALLBACK}/docs-manifest.json`)),
      map((manifest) => {
        // Keep a Set of valid page paths so direct URL/query-param navigation cannot
        // request arbitrary files from the public folder.
        const paths = new Set(flattenDocPaths(manifest.items));

        if (paths.size === 0) {
          return { status: 'empty', manifest } satisfies DocsManifestState;
        }

        return { status: 'loaded', manifest, paths } satisfies DocsManifestState;
      }),
      catchError(() =>
        of({
          status: 'error',
          message: 'Documentation is unavailable because the docs manifest could not be loaded.',
        } satisfies DocsManifestState)
      )
    );
  }

  getPage(path: string, validPaths: ReadonlySet<string>): Observable<DocsPageState> {
    if (!validPaths.has(path)) {
      return of({ status: 'not-found', path } satisfies DocsPageState);
    }

    return this.http.get(`${DOCS_BASE_URL}/${path}`, { responseType: 'text' }).pipe(
      catchError(() =>
        this.http.get(`${DOCS_BASE_URL_FALLBACK}/${path}`, { responseType: 'text' })
      ),
      switchMap((markdown) =>
        from(import('marked')).pipe(
          map(({ marked }) => {
            // Markdown files are static content, but rendered HTML is still sanitized before
            // binding through innerHTML.
            const renderedHtml = marked.parse(markdown, { async: false });
            const html = this.sanitizer.sanitize(SecurityContext.HTML, renderedHtml) ?? '';

            return { status: 'loaded', path, html } satisfies DocsPageState;
          })
        )
      ),
      catchError(() =>
        of({
          status: 'error',
          path,
          message: 'This documentation page could not be loaded.',
        } satisfies DocsPageState)
      )
    );
  }
}

// Flattening supports quick path validation while preserving the manifest as the
// source of truth for visible navigation order.
export function flattenDocPaths(items: readonly DocsNavItem[]): readonly string[] {
  return items.reduce<string[]>((paths, item) => {
    if (item.path) {
      paths.push(item.path);
    }

    paths.push(...flattenDocPaths(item.children));
    return paths;
  }, []);
}

export function findFirstDocPath(items: readonly DocsNavItem[]): string | undefined {
  for (const item of items) {
    if (item.path) {
      return item.path;
    }

    const childPath = findFirstDocPath(item.children);

    if (childPath) {
      return childPath;
    }
  }

  return undefined;
}
